import { Trash2, CheckCircle2, Clock, Pencil, FileEdit, PhoneCall, PhoneOff, PhoneOutgoing, CalendarClock, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { CallbackBlock } from "@/components/inquiry/CallbackControls";

const OUTCOME_STYLE = {
  answered: "border-[#34C759]/40 text-[#34C759]",
  claim_made: "border-[#007AFF]/50 text-[#007AFF]",
  deflected: "border-[#FFCC00]/40 text-[#FFCC00]",
  not_asked: "border-border text-muted-foreground",
};
const OUTCOME_LABEL = { answered: "Answered", claim_made: "Claim made", deflected: "Deflected", not_asked: "Not asked" };

const Badge = ({ color, Icon, label, testId }) => (
  <span className={`flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${color}`} data-testid={testId}>
    <Icon className="w-3 h-3" /> {label}
  </span>
);

const StatusBadge = ({ q }) => {
  const id = `inquiry-status-${q.id}`;
  if (q.status === "draft") return <Badge color="border-border text-muted-foreground" Icon={FileEdit} label="Draft" testId={id} />;
  if (q.status === "calling") return <Badge color="border-[#34C759]/40 text-[#34C759]" Icon={PhoneCall} label="Call in progress" testId={id} />;
  if (q.status === "called") return <Badge color="border-[#34C759]/40 text-[#34C759]" Icon={CheckCircle2} label="Call completed" testId={id} />;
  if (q.status === "call_failed") return <Badge color="border-[#FF3B30]/40 text-[#FF3B30]" Icon={PhoneOff} label="Call not completed" testId={id} />;
  if (q.status === "callback_scheduled") return <Badge color="border-[#FFCC00]/40 text-[#FFCC00]" Icon={CalendarClock} label="Callback scheduled" testId={id} />;
  if (q.status === "responded") return <Badge color="border-[#34C759]/40 text-[#34C759]" Icon={CheckCircle2} label="Responded" testId={id} />;
  return <Badge color="border-[#FFCC00]/40 text-[#FFCC00]" Icon={Clock} label="Awaiting response" testId={id} />;
};

const CallSummary = ({ q, c }) => {
  if (!c.summary && c.analyzed_at) return null;
  return (
    <div className="mb-3 border border-[#007AFF]/30 bg-[#007AFF]/5 p-3" data-testid={`inquiry-call-summary-${q.id}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#007AFF] mb-1.5 flex items-center gap-1.5">
        <Sparkles className="w-3 h-3" /> Call summary
      </p>
      {c.summary ? (
        <p className="text-sm leading-relaxed">{c.summary}</p>
      ) : (
        <p className="text-xs text-muted-foreground animate-pulse">Reviewing the transcript…</p>
      )}
      {c.outcomes?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`inquiry-call-outcomes-${q.id}`}>
          {c.outcomes.map((o, i) => (
            <span key={i} title={o.note || ""} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 border ${OUTCOME_STYLE[o.status] || OUTCOME_STYLE.not_asked}`}>
              Q{o.q ?? i + 1} · {OUTCOME_LABEL[o.status] || o.status}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const CallDetails = ({ q }) => {
  const c = q.call;
  if (!c) return null;
  const audioSrc = c.recording_stored ? `${API}/inquiries/${q.id}/recording` : c.recording_url;
  return (
    <div className="mt-4 border border-[#34C759]/30 bg-[#34C759]/5 p-4" data-testid={`inquiry-call-${q.id}`}>
      <p className="text-[10px] uppercase tracking-widest font-bold text-[#34C759] mb-2 flex items-center gap-2">
        <PhoneOutgoing className="w-3 h-3" /> {c.is_callback ? "Callback" : "Outbound call"} · {c.to_number}
        {q.call_history_count > 0 && ` · ${q.call_history_count} earlier call${q.call_history_count > 1 ? "s" : ""}`}
        {c.billed_minutes > 0 && ` · ${c.billed_minutes} min billed · $${Number(c.charged_amount || 0).toFixed(2)}`}
        {c.refunded_amount > 0 && ` · $${Number(c.refunded_amount).toFixed(2)} refunded`}
      </p>
      {c.error && <p className="text-xs text-[#FF3B30] mb-2" data-testid={`inquiry-call-error-${q.id}`}>{c.error}</p>}
      {q.status === "call_failed" && !c.error && (
        <p className="text-xs text-muted-foreground mb-2">No one answered — the full credit block was refunded.</p>
      )}
      {c.transcript && <CallSummary q={q} c={c} />}
      {c.questions?.length > 0 && (
        <ol className="text-xs text-muted-foreground list-decimal pl-4 space-y-1 mb-3">
          {c.questions.map((x, i) => <li key={i}>{x}</li>)}
        </ol>
      )}
      {c.transcript && (
        <>
          <p className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-1">Transcript</p>
          <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid={`inquiry-transcript-${q.id}`}>{c.transcript}</p>
        </>
      )}
      {audioSrc && (
        <audio controls src={audioSrc} className="mt-3 w-full h-9" data-testid={`inquiry-recording-${q.id}`} />
      )}
    </div>
  );
};

export const InquiryCard = ({ q, onDelete, onEdit, onScheduleCallback, onCancelCallback }) => (
  <div className="border border-border bg-[#0A0A0A] p-5" data-testid={`inquiry-card-${q.id}`}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="min-w-0">
        <p className="font-heading font-bold tracking-tight">{q.business_name}</p>
        <p className="text-xs text-muted-foreground font-mono">
          {q.business_email || "no email"}{q.business_phone ? ` · ${q.business_phone}` : ""} · {q.business_type}
        </p>
        {q.incident_number && (
          <p className="text-xs font-mono text-[#007AFF] mt-1">#{q.incident_number}{q.incident_title ? ` — ${q.incident_title}` : ""}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <StatusBadge q={q} />
        {q.status === "draft" && (
          <button onClick={() => onEdit(q)} title="Edit draft" data-testid={`inquiry-edit-${q.id}`}
            className="w-7 h-7 flex items-center justify-center border border-[#007AFF]/40 text-[#007AFF] hover:bg-[#007AFF]/10 rounded-sm transition-colors duration-200">
            <Pencil className="w-3.5 h-3.5" />
          </button>
        )}
        <button onClick={() => onDelete(q.id)} title="Delete inquiry" data-testid={`inquiry-delete-${q.id}`}
          disabled={q.status === "calling"}
          className="w-7 h-7 flex items-center justify-center border border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 rounded-sm transition-colors duration-200 disabled:opacity-30">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
    {q.message && (
      <p className="text-sm text-muted-foreground leading-relaxed mb-1 whitespace-pre-wrap">
        <span className="text-[10px] uppercase tracking-widest font-bold mr-2">{q.status === "draft" ? "Draft email" : "Asked"}</span>{q.message}
      </p>
    )}
    <p className="text-[10px] text-muted-foreground font-mono">
      {q.status === "draft" ? "Saved" : q.contact_method === "phone" ? "Called" : "Sent"}{" "}
      {new Date(q.status === "draft" ? (q.updated_at || q.created_at) : (q.sent_at || q.updated_at || q.created_at)).toLocaleString()}
    </p>
    <CallDetails q={q} />
    <CallbackBlock q={q} onSchedule={onScheduleCallback} onCancel={onCancelCallback} />
    {q.status === "responded" && (
      <div className="mt-4 border border-[#007AFF]/30 bg-[#007AFF]/5 p-4" data-testid={`inquiry-response-${q.id}`}>
        <p className="text-[10px] uppercase tracking-widest font-bold text-[#007AFF] mb-2">
          Their statement — {q.responder_name} · {q.responded_at && new Date(q.responded_at).toLocaleString()}
        </p>
        <p className="text-sm leading-relaxed whitespace-pre-wrap">{q.response_text}</p>
      </div>
    )}
  </div>
);
